/**
 * ContributionCard Component
 *
 * Displays a single agent contribution in the debate.
 */

import { clsx } from 'clsx';
import { ChevronDown, ChevronUp, ExternalLink, MapPin, Briefcase, Star } from 'lucide-react';
import { useState } from 'react';
import { ExpertBadge, ModeratorBadge, getExpertBgColor } from './ExpertBadge';
import type { LLMProvider, ContributionType } from '../../../lib/api';

interface Criterion {
  field: string;
  value: string;
  priority: string;
  reasoning?: string;
}

interface Disagreement {
  point: string;
  reasoning: string;
  severity: string;
}

// Provider search result (Phase 2)
interface ProviderResult {
  doctorId?: string;
  type?: 'doctor' | 'institution';
  name: string;
  title?: string;
  institution?: string;
  city?: string;
  canton?: string;
  specialty?: string;
  matchScore?: number;
  matchReasons?: string[];
  criteriaMatched?: string[];
  website?: string;
  source?: string;
}

interface SearchResults {
  webResults?: ProviderResult[];
  searchSummary?: string;
  topDoctors?: string[];
  topInstitutions?: string[];
  rankingRationale?: string;
}

interface ContributionContent {
  position?: string;
  keyPoints?: string[];
  criteria?: Criterion[];
  reasoning?: string;
  concerns?: string[];
  agreements?: string[];
  disagreements?: Disagreement[];
  missedPoints?: string[];
  overallAssessment?: string;
  positionChanged?: boolean;
  addressedCritiques?: Array<{
    critiqueFrom: LLMProvider;
    critique: string;
    response: string;
    reasoning: string;
  }>;
  consensusPoints?: string[];
  divergencePoints?: string[];
  clinicianGuidance?: string;
  // Phase 2: Provider search results
  searchResults?: SearchResults;
  providers?: ProviderResult[];
}

interface ContributionCardProps {
  model: LLMProvider;
  persona?: string | null;
  isModeratorAgent: boolean;
  contributionType: ContributionType;
  content: ContributionContent;
  confidence?: number | null;
  latencyMs?: number | null;
  tokenUsage?: { input: number; output: number } | null;
  defaultExpanded?: boolean;
}

const CONTRIBUTION_TYPE_LABELS: Record<ContributionType, { label: string; icon: string }> = {
  PROPOSAL: { label: 'Initial Proposal', icon: '📝' },
  CRITIQUE: { label: 'Peer Review', icon: '🔍' },
  DEFENSE: { label: 'Defense', icon: '🛡️' },
  CONCESSION: { label: 'Concession', icon: '🤝' },
  CLARIFICATION: { label: 'Clarification', icon: '💡' },
  SYNTHESIS: { label: 'Synthesis', icon: '⚖️' },
};

const SEVERITY_COLORS: Record<string, string> = {
  minor: 'text-yellow-600 bg-yellow-50',
  moderate: 'text-orange-600 bg-orange-50',
  major: 'text-red-600 bg-red-50',
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'text-red-700 bg-red-100',
  high: 'text-orange-700 bg-orange-100',
  medium: 'text-blue-700 bg-blue-100',
  low: 'text-gray-700 bg-gray-100',
};

/**
 * ProviderCard - displays a single provider result (same layout as validation Phase 2)
 */
function ProviderCard({ provider, index }: { provider: ProviderResult; index: number }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100">
      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-medium">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-medium text-gray-900 truncate">{provider.name}</p>
            {provider.title && (
              <p className="text-xs text-gray-500 truncate">{provider.title}</p>
            )}
          </div>
          {provider.website && (
            <a
              href={provider.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-600"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
        {provider.institution && (
          <p className="text-xs text-emerald-600 truncate mt-0.5">{provider.institution}</p>
        )}
        <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500">
          {provider.specialty && (
            <span className="flex items-center gap-1">
              <Briefcase className="h-3 w-3" />
              {provider.specialty}
            </span>
          )}
          {(provider.city || provider.canton) && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {[provider.city, provider.canton].filter(Boolean).join(', ')}
            </span>
          )}
          {provider.matchScore !== undefined && (
            <span className="flex items-center gap-1">
              <Star className="h-3 w-3 text-amber-500" />
              {provider.matchScore}%
            </span>
          )}
        </div>
        {provider.matchReasons && provider.matchReasons.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {provider.matchReasons.slice(0, 3).map((reason, i) => (
              <span
                key={i}
                className="inline-flex px-1.5 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded"
              >
                {reason}
              </span>
            ))}
            {provider.matchReasons.length > 3 && (
              <span className="text-xs text-gray-400">
                +{provider.matchReasons.length - 3} more
              </span>
            )}
          </div>
        )}
        {provider.criteriaMatched && provider.criteriaMatched.length > 0 && !provider.matchReasons?.length && (
          <div className="mt-2 flex flex-wrap gap-1">
            {provider.criteriaMatched.slice(0, 3).map((criterion, i) => (
              <span
                key={i}
                className="inline-flex px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded"
              >
                ✓ {criterion}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ContributionCard({
  model,
  persona,
  isModeratorAgent,
  contributionType,
  content,
  confidence,
  latencyMs,
  tokenUsage,
  defaultExpanded = false,
}: ContributionCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const typeConfig = CONTRIBUTION_TYPE_LABELS[contributionType] || { label: contributionType, icon: '💬' };

  const bgColor = isModeratorAgent ? 'bg-indigo-50' : getExpertBgColor(model);

  return (
    <div className={clsx('rounded-lg border', bgColor, 'border-gray-200')}>
      {/* Header */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isModeratorAgent ? (
            <ModeratorBadge size="sm" />
          ) : (
            <ExpertBadge model={model} persona={persona} size="sm" />
          )}
          <span className="text-sm text-gray-500">
            {typeConfig.icon} {typeConfig.label}
          </span>
        </div>

        <div className="flex items-center gap-4">
          {confidence != null && (
            <span className="text-sm font-medium text-gray-600">
              {confidence}% confidence
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 p-4 space-y-4">
          {/* Position */}
          {content.position && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Position</h4>
              <p className="text-sm text-gray-600">{content.position}</p>
            </div>
          )}

          {/* Key Points */}
          {content.keyPoints && content.keyPoints.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Points</h4>
              <ul className="space-y-1">
                {content.keyPoints.map((point, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-gray-400">•</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Matching Criteria */}
          {content.criteria && content.criteria.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Matching Criteria</h4>
              <div className="space-y-2">
                {content.criteria.map((crit, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span
                      className={clsx(
                        'px-1.5 py-0.5 rounded text-xs font-medium',
                        PRIORITY_COLORS[crit.priority?.toLowerCase()] || PRIORITY_COLORS.medium
                      )}
                    >
                      {crit.priority?.toUpperCase()}
                    </span>
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-700">{crit.field}:</span>{' '}
                      <span className="text-sm text-gray-600">{crit.value}</span>
                      {crit.reasoning && (
                        <p className="text-xs text-gray-500 mt-0.5 italic">{crit.reasoning}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Phase 4: Care Provider Matching Results */}
          {(() => {
            const providers = content.searchResults?.webResults || content.providers || [];
            const searchSummary = content.searchResults?.searchSummary;
            const topDoctors = content.searchResults?.topDoctors;
            const topInstitutions = content.searchResults?.topInstitutions;
            const rankingRationale = content.searchResults?.rankingRationale;

            if (providers.length === 0) return null;

            return (
              <div>
                <h4 className="text-sm font-semibold text-emerald-700 mb-2">
                  🏥 Provider Recommendations ({providers.length})
                </h4>

                {/* Top Picks Summary */}
                {(topDoctors?.length || topInstitutions?.length) && (
                  <div className="mb-3 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
                    {topDoctors && topDoctors.length > 0 && (
                      <div className="text-xs text-emerald-700">
                        <span className="font-medium">Top Care Providers:</span> {topDoctors.join(', ')}
                      </div>
                    )}
                    {topInstitutions && topInstitutions.length > 0 && (
                      <div className="text-xs text-emerald-700 mt-1">
                        <span className="font-medium">Top Institutions:</span> {topInstitutions.join(', ')}
                      </div>
                    )}
                  </div>
                )}

                {/* Search Summary */}
                {searchSummary && (
                  <p className="text-xs text-gray-500 italic mb-2">{searchSummary}</p>
                )}

                {/* Provider List */}
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {providers.map((provider, idx) => (
                    <ProviderCard key={idx} provider={provider} index={idx} />
                  ))}
                </div>

                {/* Ranking Rationale */}
                {rankingRationale && (
                  <div className="mt-3 p-2 bg-gray-50 rounded-lg border border-gray-100">
                    <h5 className="text-xs font-medium text-gray-600 mb-1">Ranking Rationale</h5>
                    <p className="text-xs text-gray-500">{rankingRationale}</p>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Agreements (for critiques) */}
          {content.agreements && content.agreements.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-700 mb-2">✓ Agreements</h4>
              <ul className="space-y-1">
                {content.agreements.map((agreement, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-green-500">•</span>
                    {agreement}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Disagreements (for critiques) */}
          {content.disagreements && content.disagreements.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-red-700 mb-2">✗ Disagreements</h4>
              <div className="space-y-2">
                {content.disagreements.map((d, i) => (
                  <div key={i} className="border-l-2 border-red-200 pl-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-xs font-medium',
                          SEVERITY_COLORS[d.severity?.toLowerCase()] || SEVERITY_COLORS.moderate
                        )}
                      >
                        {d.severity?.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium text-gray-700">{d.point}</span>
                    </div>
                    <p className="text-sm text-gray-600">{d.reasoning}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Addressed Critiques (for defense) */}
          {content.addressedCritiques && content.addressedCritiques.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Responses to Critiques</h4>
              <div className="space-y-3">
                {content.addressedCritiques.map((ac, i) => (
                  <div key={i} className="border-l-2 border-gray-200 pl-3">
                    <div className="flex items-center gap-2 mb-1">
                      <ExpertBadge model={ac.critiqueFrom} size="sm" showIcon={false} />
                      <span
                        className={clsx(
                          'px-1.5 py-0.5 rounded text-xs font-medium',
                          ac.response === 'accept'
                            ? 'text-green-700 bg-green-100'
                            : ac.response === 'reject'
                              ? 'text-red-700 bg-red-100'
                              : 'text-yellow-700 bg-yellow-100'
                        )}
                      >
                        {ac.response.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{ac.reasoning}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consensus Points (for synthesis) */}
          {content.consensusPoints && content.consensusPoints.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-green-700 mb-2">✅ Consensus Points</h4>
              <ul className="space-y-1">
                {content.consensusPoints.map((point, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-green-500">•</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Divergence Points (for synthesis) */}
          {content.divergencePoints && content.divergencePoints.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-amber-700 mb-2">⚠️ Divergence Points</h4>
              <ul className="space-y-1">
                {content.divergencePoints.map((point, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Clinician Guidance */}
          {content.clinicianGuidance && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <h4 className="text-sm font-semibold text-blue-800 mb-1">Guidance for Clinician</h4>
              <div className="text-sm text-blue-700 space-y-2">
                <FormattedGuidance text={content.clinicianGuidance} />
              </div>
            </div>
          )}

          {/* Concerns */}
          {content.concerns && content.concerns.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-amber-700 mb-2">Concerns</h4>
              <ul className="space-y-1">
                {content.concerns.map((concern, i) => (
                  <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                    <span className="text-amber-500">⚠</span>
                    {concern}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Reasoning */}
          {content.reasoning && (
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-1">Detailed Reasoning</h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{content.reasoning}</p>
            </div>
          )}

          {/* Metadata */}
          {(latencyMs || tokenUsage) && (
            <div className="flex items-center gap-4 pt-2 border-t border-gray-100 text-xs text-gray-400">
              {latencyMs && <span>{(latencyMs / 1000).toFixed(1)}s</span>}
              {tokenUsage && (
                <span>
                  {tokenUsage.input + tokenUsage.output} tokens
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Helper component to format clinician guidance text with basic markdown support
 */
function FormattedGuidance({ text }: { text: string }) {
  // Split by double newlines for paragraphs, or single newlines with ** markers for sections
  const sections = text.split(/\n(?=\*\*)|(?:\n\n)/g).filter(Boolean);

  return (
    <>
      {sections.map((section, idx) => {
        // Replace **text** with bold spans
        const formattedParts: React.ReactNode[] = [];
        const parts = section.split(/(\*\*[^*]+\*\*)/g);

        parts.forEach((part, partIdx) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            // Bold text
            const boldText = part.slice(2, -2);
            formattedParts.push(
              <strong key={partIdx} className="font-semibold text-blue-800">
                {boldText}
              </strong>
            );
          } else if (part.trim()) {
            formattedParts.push(<span key={partIdx}>{part}</span>);
          }
        });

        return (
          <p key={idx} className="leading-relaxed">
            {formattedParts}
          </p>
        );
      })}
    </>
  );
}
